import React, { useState, useEffect } from 'react';
import { ClipboardList, CheckCircle2, ChevronRight, ChevronLeft, Loader2, ShieldCheck } from 'lucide-react';

// ── Reusable input components ────────────────────────────────────────────────

const ScaleInput = ({ value, onChange, leftLabel, rightLabel }) => (
  <div>
    <div className="flex justify-between mb-3">
      <span className="text-sm text-gray-500">{leftLabel}</span>
      <span className="text-sm text-gray-500">{rightLabel}</span>
    </div>
    <div className="flex gap-3 justify-center">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-12 h-12 rounded-2xl font-bold text-lg transition-all ${
            value === n
              ? 'bg-blue-600 text-white shadow-lg scale-110'
              : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-300'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

const RadioGroup = ({ options, value, onChange }) => (
  <div className="space-y-3">
    {options.map(option => (
      <label
        key={option}
        className={`flex items-center space-x-3 cursor-pointer p-4 rounded-2xl border transition-all ${
          value === option ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'
        }`}
      >
        <input type="radio" value={option} checked={value === option} onChange={() => onChange(option)} className="text-blue-600 w-4 h-4" />
        <span className="font-medium text-gray-800">{option}</span>
      </label>
    ))}
  </div>
);

const MultiSelect = ({ options, values, onToggle }) => (
  <div className="space-y-3">
    {options.map(option => (
      <label
        key={option}
        className={`flex items-center space-x-3 cursor-pointer p-4 rounded-2xl border transition-all ${
          values.includes(option) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'
        }`}
      >
        <input type="checkbox" checked={values.includes(option)} onChange={() => onToggle(option)} className="text-blue-600 w-4 h-4 rounded" />
        <span className="font-medium text-gray-800">{option}</span>
      </label>
    ))}
  </div>
);

const Question = ({ number, text, hint, children }) => (
  <div className="mb-8">
    <div className="flex items-start gap-3 mb-4">
      <span className="bg-blue-100 text-blue-700 text-sm font-bold px-2.5 py-1 rounded-xl min-w-[2rem] text-center shrink-0">
        {number}
      </span>
      <div>
        <p className="text-gray-900 font-semibold text-lg leading-snug">{text}</p>
        {hint && <p className="text-sm text-gray-500 mt-1">{hint}</p>}
      </div>
    </div>
    {children}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const SurveyContent = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [respondentId, setRespondentId] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    fetch('/api/questions')
      .then(r => r.json())
      .then(qs => {
        setQuestions(qs);
        const initial = {};
        qs.forEach(q => {
          if (q.type === 'multiselect') initial[q.fieldName] = [];
          else if (q.type === 'scale') initial[q.fieldName] = 0;
          else initial[q.fieldName] = '';
        });
        setAnswers(initial);
        setLoading(false);
      })
      .catch(() => {
        setLoadError('Could not load survey questions. Please refresh the page.');
        setLoading(false);
      });
  }, []);

  // Group questions into steps
  const steps = [];
  questions.forEach(q => {
    let s = steps.find(x => x.step === q.step);
    if (!s) { s = { step: q.step, title: q.stepTitle, subtitle: q.stepSubtitle, questions: [] }; steps.push(s); }
    s.questions.push(q);
  });
  steps.sort((a, b) => a.step - b.step);

  const totalSteps = steps.length;

  const isAnswered = (q) => {
    const val = answers[q.fieldName];
    if (q.type === 'multiselect') return Array.isArray(val) && val.length > 0;
    if (q.type === 'scale') return val > 0;
    return val && val.toString().trim() !== '';
  };

  const stepValid = () => {
    const current = steps[step - 1];
    if (!current) return false;
    return current.questions.filter(q => q.required).every(isAnswered);
  };

  const set = (field, value) => setAnswers(prev => ({ ...prev, [field]: value }));
  const toggle = (field, value) =>
    setAnswers(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));

  const advance = (dir) => {
    setStep(s => s + dir);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch('/api/survey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, consent })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRespondentId(data.respondentId);
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (q) => {
    switch (q.type) {
      case 'radio':
        return (
          <Question key={q.fieldName} number={q.order} text={q.text} hint={q.hint}>
            <RadioGroup options={q.options} value={answers[q.fieldName] || ''} onChange={v => set(q.fieldName, v)} />
          </Question>
        );
      case 'multiselect':
        return (
          <Question key={q.fieldName} number={q.order} text={q.text} hint={q.hint}>
            <MultiSelect options={q.options} values={answers[q.fieldName] || []} onToggle={v => toggle(q.fieldName, v)} />
          </Question>
        );
      case 'scale':
        return (
          <Question key={q.fieldName} number={q.order} text={q.text} hint={q.hint}>
            <ScaleInput value={answers[q.fieldName] || 0} onChange={v => set(q.fieldName, v)} leftLabel={q.scaleLeft} rightLabel={q.scaleRight} />
          </Question>
        );
      case 'textarea':
        return (
          <Question key={q.fieldName} number={q.order} text={q.text} hint={q.hint}>
            <textarea
              value={answers[q.fieldName] || ''}
              onChange={e => set(q.fieldName, e.target.value)}
              placeholder="Share your thoughts..."
              rows={5}
              className="w-full p-4 rounded-2xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-none text-gray-800 placeholder-gray-400"
            />
          </Question>
        );
      default:
        return null;
    }
  };

  // ── Loading state ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Loader2 className="text-blue-600 animate-spin mb-4" size={36} />
        <p className="text-gray-500">Loading survey...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-3xl text-center">
        {loadError}
      </div>
    );
  }

  // ── Success state ────────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-lg">
          <CheckCircle2 className="text-green-600" size={40} />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Thank You!</h2>
        <p className="text-gray-600 text-lg mb-2">Your response has been recorded.</p>
        <p className="text-sm text-gray-400 mb-6">Response ID: {respondentId}</p>
        <div className="bg-blue-50 border border-blue-100 rounded-3xl p-8 max-w-md">
          <p className="text-gray-700 leading-relaxed">
            Your voice matters. Responses like yours help shape conversations about immigration policy at the local, state, and national level.
          </p>
        </div>
      </div>
    );
  }

  const currentStep = steps[step - 1];

  // ── Survey UI ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8">
      <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-white/20 rounded-2xl">
            <ClipboardList size={24} />
          </div>
          <h2 className="text-3xl font-bold">Immigration Survey</h2>
        </div>
        <p className="opacity-90 text-lg">High School Students' Views on U.S. Immigration Policy</p>
        <div className="flex items-start gap-2 mt-4 pt-4 border-t border-white/20">
          <ShieldCheck size={18} className="shrink-0 mt-0.5 opacity-80" />
          <p className="text-sm opacity-80 leading-relaxed">
            Anonymous — we do not ask for your name, email, or school name, and we do
            not store your IP address with your answers. You can stop at any time, and
            every question except the ones marked required can be skipped.
          </p>
        </div>
      </div>

      <div className="bg-white/90 backdrop-blur-sm p-6 rounded-3xl shadow-lg border border-gray-100/50">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-600">Step {step} of {totalSteps}</span>
          <span className="text-sm font-medium text-blue-600">{Math.round((step / totalSteps) * 100)}% complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${(step / totalSteps) * 100}%` }} />
        </div>
        <div className="mt-4">
          <h3 className="text-xl font-bold text-gray-900">{currentStep?.title}</h3>
          <p className="text-gray-500 mt-1">{currentStep?.subtitle}</p>
        </div>
      </div>

      <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50">
        {currentStep?.questions.map(renderQuestion)}

        {step === totalSteps && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={e => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded text-blue-600 shrink-0"
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                I understand my answers are submitted anonymously and will be used only
                for research on students' views about immigration policy. I have not
                included my name or contact details in any written answer.
              </span>
            </label>
          </div>
        )}

        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl mb-6">
            {submitError}
          </div>
        )}

        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
          {step > 1 ? (
            <button
              onClick={() => advance(-1)}
              className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-2xl font-semibold border border-gray-200 hover:bg-white/80 transition-all"
            >
              <ChevronLeft size={18} />
              <span>Back</span>
            </button>
          ) : <div />}

          {step < totalSteps ? (
            <button
              onClick={() => advance(1)}
              disabled={!stepValid()}
              className="flex items-center space-x-2 bg-blue-600 text-white px-8 py-3 rounded-2xl font-semibold hover:bg-blue-700 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <span>Next</span>
              <ChevronRight size={18} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!stepValid() || !consent || submitting}
              className="flex items-center space-x-2 bg-green-600 text-white px-8 py-3 rounded-2xl font-semibold hover:bg-green-700 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
              <span>{submitting ? 'Submitting...' : 'Submit Survey'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyContent;
